import React, { useEffect, useMemo, useRef, useState } from "react";
import { UploadCloud, Play, Pause, Scissors, AudioLines, MapPin, Repeat, Save, Trash2 } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Field, inputCls } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const CHANNELS = ["Drums", "Bass", "Keys", "Guitars", "Percussion", "Vocals", "FX", "Click", "Guide", "Pads", "Other"];
const SECTIONS = ["Intro", "Verse", "Chorus", "Bridge", "Instrumental", "Turnaround", "Outro"];
const SECTION_COLOR = { Intro: "#4E97FF", Verse: "#7C5CFF", Chorus: "#E5C378", Bridge: "#EC6A9C", Instrumental: "#2FBF9B", Turnaround: "#F59E0B", Outro: "#8A97AC" };

function fmtTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function UploadDialog({ songs, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ name: "", song_id: "", key: "", bpm: 0, channel: "Other" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!file) { toast.error("Choose an audio file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", f.name || file.name);
      fd.append("song_id", f.song_id); fd.append("key", f.key); fd.append("bpm", f.bpm); fd.append("channel", f.channel);
      await api.post("/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Track uploaded"); setOpen(false); setFile(null); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="upload-track-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm"><UploadCloud className="w-4 h-4" /> Upload track</button></DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-md">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Upload backing track / stem</DialogTitle></DialogHeader>
        <div className="text-xs text-[#6E7D97] mb-1">WAV, MP3, M4A, FLAC or OGG. Only upload material you're licensed to use.</div>
        <input type="file" accept=".wav,.mp3,.m4a,.flac,.ogg" onChange={(e) => setFile(e.target.files[0])} data-testid="track-file-input"
          className="block w-full text-sm text-[#B8C1D1] file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E5C378] file:text-[#0A0E1A] file:font-semibold" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Track name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Drums stem" /></Field>
          <Field label="Channel"><select className={inputCls} value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Song"><select className={inputCls} value={f.song_id} onChange={(e) => setF({ ...f, song_id: e.target.value })}><option value="">(Library)</option>{songs.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}</select></Field>
          <Field label="Key / BPM"><div className="flex gap-2"><input className={inputCls} value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="Key" /><input type="number" className={inputCls} value={f.bpm || ""} onChange={(e) => setF({ ...f, bpm: Number(e.target.value) })} placeholder="BPM" /></div></Field>
        </div>
        <button onClick={save} disabled={busy} data-testid="save-track-button" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm disabled:opacity-60">{busy ? "Uploading…" : "Upload"}</button>
      </DialogContent>
    </Dialog>
  );
}

function Mixer({ tracks, canManage, onDelete }) {
  const firstId = tracks[0]?.id;
  const [state, setState] = useState(() => Object.fromEntries(tracks.map((t) => [t.id, { mute: false, solo: false, vol: 0.9 }])));
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [pos, setPos] = useState(0);
  const [markers, setMarkers] = useState(tracks[0]?.markers || []);
  const [loop, setLoop] = useState(tracks[0]?.loop && Object.keys(tracks[0].loop).length ? tracks[0].loop : { enabled: false, start: 0, end: 0 });
  const [section, setSection] = useState("Chorus");
  const [dirty, setDirty] = useState(false);
  const refs = useRef({});
  const barRef = useRef(null);
  const anySolo = Object.values(state).some((s) => s.solo);

  useEffect(() => {
    setState((prev) => {
      const next = { ...prev };
      tracks.forEach((t) => { if (!next[t.id]) next[t.id] = { mute: false, solo: false, vol: 0.9 }; });
      return next;
    });
    setMarkers(tracks[0]?.markers || []);
    setLoop(tracks[0]?.loop && Object.keys(tracks[0].loop).length ? tracks[0].loop : { enabled: false, start: 0, end: 0 });
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstId]);

  useEffect(() => {
    tracks.forEach((t) => {
      const el = refs.current[t.id]; if (!el) return;
      const s = state[t.id] || {};
      el.volume = Number.isFinite(s.vol) ? s.vol : 0.9;
      const audible = (anySolo ? s.solo : true) && !s.mute;
      el.muted = !audible;
    });
  }, [state, anySolo, tracks]);

  // playhead + loop enforcement
  useEffect(() => {
    if (!playing) return;
    let raf;
    const tick = () => {
      const el = refs.current[firstId];
      if (el) {
        let t = el.currentTime;
        if (loop.enabled && loop.end > loop.start && t >= loop.end) {
          tracks.forEach((tk) => { const e = refs.current[tk.id]; if (e) e.currentTime = loop.start; });
          t = loop.start;
        }
        setPos(t);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, loop, firstId, tracks]);

  const onMeta = (id, el) => { if (el && Number.isFinite(el.duration)) setDur((d) => Math.max(d, el.duration)); };

  const toggleAll = async () => {
    if (playing) {
      tracks.forEach((t) => { const el = refs.current[t.id]; if (el) el.pause(); });
      setPlaying(false);
    } else {
      const start = loop.enabled && loop.end > loop.start ? loop.start : pos || 0;
      await Promise.all(tracks.map((t) => { const el = refs.current[t.id]; if (el) { el.currentTime = start; return el.play().catch(() => {}); } return null; }));
      setPlaying(true);
    }
  };
  const seek = (t) => { tracks.forEach((tk) => { const e = refs.current[tk.id]; if (e) e.currentTime = t; }); setPos(t); };
  const upd = (id, patch) => setState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const onBarClick = (e) => {
    if (!dur || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    seek(ratio * dur);
  };
  const addMarker = () => { setMarkers([...markers, { id: crypto.randomUUID(), time: pos, label: section }].sort((a, b) => a.time - b.time)); setDirty(true); };
  const removeMarker = (id) => { setMarkers(markers.filter((m) => m.id !== id)); setDirty(true); };
  const setLoopIn = () => { setLoop({ ...loop, start: pos, enabled: true }); setDirty(true); };
  const setLoopOut = () => { setLoop({ ...loop, end: pos, enabled: true }); setDirty(true); };
  const toggleLoop = () => { setLoop({ ...loop, enabled: !loop.enabled }); setDirty(true); };
  const clearLoop = () => { setLoop({ enabled: false, start: 0, end: 0 }); setDirty(true); };
  const saveBuilder = async () => {
    try { await api.put(`/tracks/${firstId}/builder`, { markers, loop }); toast.success("Arrangement saved"); setDirty(false); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pct = (t) => (dur ? Math.min(100, (t / dur) * 100) : 0);
  const showTimeline = dur > 0 && (canManage || markers.length > 0 || loop.enabled);

  return (
    <div className="bg-[#080B14] border border-white/10 rounded-2xl p-5 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-[#F7F5F0]">Mixer · {tracks[0]?.song_id ? "Song stems" : "Tracks"}</h3>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-[#8A97AC]">{fmtTime(pos)} / {fmtTime(dur)}</span>
          <button onClick={toggleAll} data-testid="mixer-play-toggle" className="flex items-center gap-2 px-5 py-2.5 rounded-xl wf-gold-btn font-bold">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "Stop" : "Play all"}
          </button>
        </div>
      </div>

      {showTimeline && (
        <div className="mb-4">
          {/* section markers row */}
          <div className="relative h-5 mb-1">
            {markers.map((m) => (
              <div key={m.id} className="absolute -translate-x-1/2 group" style={{ left: `${pct(m.time)}%` }}>
                <span className="text-[9px] font-semibold px-1 rounded" style={{ color: SECTION_COLOR[m.label] || "#E5C378" }}>{m.label}</span>
                {canManage && <button onClick={() => removeMarker(m.id)} className="hidden group-hover:inline text-[9px] text-rose-400 ml-0.5">✕</button>}
              </div>
            ))}
          </div>
          {/* timeline bar */}
          <div ref={barRef} onClick={onBarClick} data-testid="track-timeline"
            className="relative h-9 rounded-lg bg-[#0D1424] border border-white/10 cursor-pointer overflow-hidden">
            {loop.enabled && loop.end > loop.start && (
              <div className="absolute top-0 bottom-0 bg-[#E5C378]/15 border-x border-[#E5C378]/50" style={{ left: `${pct(loop.start)}%`, width: `${pct(loop.end) - pct(loop.start)}%` }} />
            )}
            {markers.map((m) => (
              <div key={m.id} className="absolute top-0 bottom-0 w-0.5" style={{ left: `${pct(m.time)}%`, background: SECTION_COLOR[m.label] || "#E5C378" }} />
            ))}
            <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_8px_#fff]" style={{ left: `${pct(pos)}%` }} />
          </div>
          {canManage && (
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="marker-section-select"
                className="bg-[#0B101D] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-[#F7F5F0]">
                {SECTIONS.map((sname) => <option key={sname}>{sname}</option>)}
              </select>
              <button onClick={addMarker} data-testid="add-marker" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#F7F5F0]"><MapPin className="w-3.5 h-3.5" /> Add marker</button>
              <span className="w-px h-5 bg-white/10" />
              <button onClick={setLoopIn} data-testid="loop-in" className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#F7F5F0]">Loop in</button>
              <button onClick={setLoopOut} data-testid="loop-out" className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#F7F5F0]">Loop out</button>
              <button onClick={toggleLoop} data-testid="loop-toggle" className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs border ${loop.enabled ? "bg-[#E5C378]/20 text-[#E5C378] border-[#E5C378]/40" : "bg-white/5 text-[#8A97AC] border-white/10"}`}><Repeat className="w-3.5 h-3.5" /> Loop {loop.enabled ? "on" : "off"}</button>
              {(loop.start || loop.end) ? <button onClick={clearLoop} className="text-xs text-[#6E7D97] hover:text-rose-400">clear</button> : null}
              <button onClick={saveBuilder} data-testid="save-builder" className={`ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${dirty ? "wf-gold-btn" : "bg-white/5 border border-white/10 text-[#8A97AC]"}`}><Save className="w-3.5 h-3.5" /> Save</button>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-2">
        {tracks.map((t) => {
          const s = state[t.id] || {};
          return (
            <div key={t.id} className="bg-[#0D1424] border border-white/5 rounded-xl p-3 flex flex-col items-center gap-3 w-32 shrink-0">
              <audio ref={(el) => { refs.current[t.id] = el; }} src={fileUrl(t.storage_path)} preload="metadata" loop
                onLoadedMetadata={(e) => onMeta(t.id, e.currentTarget)} />
              <div className="text-xs text-[#F7F5F0] font-medium text-center truncate w-full">{t.name}</div>
              <div className="text-[10px] text-[#E5C378]">{t.channel}</div>
              <input type="range" min="0" max="1" step="0.01" value={s.vol ?? 0.9} onChange={(e) => upd(t.id, { vol: Number(e.target.value) })}
                className="w-full accent-[#E5C378]" data-testid={`track-vol-${t.id}`} />
              <div className="flex gap-1.5 w-full">
                <button onClick={() => upd(t.id, { mute: !s.mute })} data-testid={`track-mute-${t.id}`} className={`flex-1 py-1 rounded-md text-xs font-bold border ${s.mute ? "bg-rose-500/20 text-rose-300 border-rose-500/40" : "bg-white/5 text-[#8A97AC] border-white/10"}`}>M</button>
                <button onClick={() => upd(t.id, { solo: !s.solo })} data-testid={`track-solo-${t.id}`} className={`flex-1 py-1 rounded-md text-xs font-bold border ${s.solo ? "bg-[#E5C378]/20 text-[#E5C378] border-[#E5C378]/40" : "bg-white/5 text-[#8A97AC] border-white/10"}`}>S</button>
              </div>
              {canManage && <button onClick={() => onDelete(t.id)} data-testid={`track-delete-${t.id}`} className="w-full py-1 rounded-md text-xs bg-white/5 border border-white/10 text-[#8A97AC] hover:text-rose-300 hover:border-rose-500/40">Delete</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Tracks() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [songs, setSongs] = useState([]);
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);
  const [sep, setSep] = useState({});
  const load = () => api.get("/tracks").then((r) => setTracks(r.data));
  useEffect(() => { load(); api.get("/songs").then((r) => setSongs(r.data)); }, []);

  useEffect(() => {
    const active = Object.entries(sep).filter(([, st]) => ["starting", "processing"].includes(st)).map(([id]) => id);
    if (active.length === 0) return;
    const iv = setInterval(async () => {
      for (const id of active) {
        try {
          const { data } = await api.get(`/tracks/${id}/separation`);
          setSep((s) => ({ ...s, [id]: data.status }));
          if (data.status === "succeeded") { toast.success("Stems are ready"); load(); }
          if (data.status === "failed") { toast.error("Separation failed: " + (data.error || "unknown error")); }
        } catch (e) { /* keep polling */ }
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [sep]);

  const groups = useMemo(() => {
    const g = {};
    tracks.forEach((t) => { const k = t.song_id || "library"; (g[k] = g[k] || []).push(t); });
    return g;
  }, [tracks]);
  const songName = (id) => songs.find((s) => s.id === id)?.title || "Track Library";

  const separate = async (id) => {
    try {
      const { data } = await api.post(`/tracks/${id}/separate`);
      setSep((s) => ({ ...s, [id]: data.status }));
      if (data.status === "requires_configuration") toast.info(data.message);
      else toast.success(data.message || "Separation started");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteTrack = async (id) => {
    if (!window.confirm("Delete this track?")) return;
    try { await api.delete(`/tracks/${id}`); toast.success("Track deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Rehearse to the record" title="Track Studio"
        subtitle="Upload tracks and stems, mark out the arrangement, loop a section, and mix your part."
        actions={canManage && <UploadDialog songs={songs} onDone={load} />} />

      {tracks.length === 0 ? (
        <EmptyState icon={AudioLines} title="No tracks available" body="Upload a track or stem to rehearse to, or connect a supported source later." />
      ) : (
        <div className="space-y-8">
          {Object.entries(groups).map(([sid, list]) => (
            <div key={sid}>
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">{sid === "library" ? "Library" : songName(sid)}</div>
                {canManage && list[0] && (() => {
                  const st = sep[list[0].id];
                  const busy = ["starting", "processing"].includes(st);
                  return (
                    <button onClick={() => !busy && separate(list[0].id)} disabled={busy} data-testid="separate-stems-button"
                      className="text-xs text-[#8A97AC] hover:text-[#E5C378] flex items-center gap-1 disabled:opacity-70">
                      <Scissors className="w-3.5 h-3.5" /> {busy ? "Separating stems… (1-3 min)" : st === "requires_configuration" ? "Connect Replicate to enable" : "Separate stems"}
                    </button>
                  );
                })()}
              </div>
              <Mixer tracks={list} canManage={canManage} onDelete={deleteTrack} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
